import type { Session } from '@supabase/supabase-js'

import { isDesktopHost } from '@/lib/desktop-host'
import {
  type DesktopSessionSnapshot,
  loadDesktopSessionSnapshot
} from '@/lib/desktop-session-access'
import { supabase } from '@/lib/supabase/browser-client'

const DESKTOP_BROWSER_SESSION_HYDRATE_TIMEOUT_MS = 10000
const DESKTOP_BROWSER_SESSION_CACHE_TTL_MS = 15000

let pendingDesktopHostedBrowserSessionHydration: Promise<Session | null> | null = null
let pendingDesktopHostedBrowserSessionHydrationKey = ''
let cachedDesktopHostedBrowserSessionHydration:
  | {
      key: string
      session: Session | null
      expiresAt: number
    }
  | null = null

function sanitizeString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function sanitizeNumber(value: unknown) {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

async function applyDesktopSessionTokens(accessToken: string, refreshToken: string) {
  const result = await supabase.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken
  })

  if (result.error) {
    throw result.error
  }

  return result.data.session || null
}

function buildFallbackDesktopSession(desktopSession: DesktopSessionSnapshot | null): Session | null {
  const userId = sanitizeString(desktopSession?.user?.id)
  const accessToken = sanitizeString(desktopSession?.sessionData?.accessToken)
  const refreshToken = sanitizeString(desktopSession?.sessionData?.refreshToken)

  if (
    !desktopSession?.isAuthenticated ||
    !userId ||
    !accessToken ||
    !refreshToken
  ) {
    return null
  }

  const email = sanitizeString(desktopSession?.user?.email) || undefined
  const tokenType = sanitizeString(desktopSession?.sessionData?.tokenType) || 'bearer'
  const expiresAt = sanitizeNumber(desktopSession?.sessionData?.expiresAt) ?? undefined
  const expiresIn = sanitizeNumber(desktopSession?.sessionData?.expiresIn) ?? undefined

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
    token_type: tokenType,
    expires_at: expiresAt,
    expires_in: expiresIn,
    user: {
      id: userId,
      email,
      aud: 'authenticated',
      role: 'authenticated',
      app_metadata: {},
      user_metadata: {}
    } as Session['user']
  } as Session
}

function buildDesktopHostedBrowserSessionHydrationKey(
  desktopSession: DesktopSessionSnapshot | null
): string {
  const userId = sanitizeString(desktopSession?.user?.id)
  const accessToken = sanitizeString(desktopSession?.sessionData?.accessToken)
  const refreshToken = sanitizeString(desktopSession?.sessionData?.refreshToken)
  const expiresAt = sanitizeNumber(desktopSession?.sessionData?.expiresAt) ?? 0

  if (
    !desktopSession?.isAuthenticated ||
    !userId ||
    !accessToken ||
    !refreshToken
  ) {
    return ''
  }

  return [userId, accessToken, refreshToken, String(expiresAt)].join(':')
}

function getCachedDesktopHostedBrowserSession(hydrationKey: string): Session | null | undefined {
  if (!hydrationKey || !cachedDesktopHostedBrowserSessionHydration) {
    return undefined
  }

  if (
    cachedDesktopHostedBrowserSessionHydration.key !== hydrationKey ||
    cachedDesktopHostedBrowserSessionHydration.expiresAt <= Date.now()
  ) {
    cachedDesktopHostedBrowserSessionHydration = null
    return undefined
  }

  return cachedDesktopHostedBrowserSessionHydration.session
}

function cacheDesktopHostedBrowserSession(hydrationKey: string, session: Session | null) {
  if (!hydrationKey) {
    return
  }

  cachedDesktopHostedBrowserSessionHydration = {
    key: hydrationKey,
    session,
    expiresAt: Date.now() + DESKTOP_BROWSER_SESSION_CACHE_TTL_MS
  }
}

export function resetDesktopHostedBrowserSessionHydrationState() {
  pendingDesktopHostedBrowserSessionHydration = null
  pendingDesktopHostedBrowserSessionHydrationKey = ''
  cachedDesktopHostedBrowserSessionHydration = null
}

async function resolveDesktopHydratedSession(
  desktopSession: DesktopSessionSnapshot | null
): Promise<Session | null> {
  const accessToken = sanitizeString(desktopSession?.sessionData?.accessToken)
  const refreshToken = sanitizeString(desktopSession?.sessionData?.refreshToken)
  const fallbackSession = buildFallbackDesktopSession(desktopSession)

  if (!accessToken || !refreshToken) {
    return fallbackSession
  }

  const hydrationPromise = applyDesktopSessionTokens(accessToken, refreshToken).catch((error) => {
    console.warn('Failed to hydrate hosted desktop browser session:', error)
    return fallbackSession
  })

  const timeoutPromise = new Promise<Session | null>((resolve) => {
    window.setTimeout(() => resolve(fallbackSession), DESKTOP_BROWSER_SESSION_HYDRATE_TIMEOUT_MS)
  })

  const hydratedSession = await Promise.race([hydrationPromise, timeoutPromise])
  return hydratedSession || fallbackSession
}

export async function hydrateDesktopHostedBrowserSession(
  desktopSessionInput?: DesktopSessionSnapshot | null
): Promise<Session | null> {
  if (typeof window === 'undefined' || !isDesktopHost()) {
    return null
  }

  const desktopSession =
    desktopSessionInput ||
    (await loadDesktopSessionSnapshot({
      includeTokens: true,
      timeoutMs: DESKTOP_BROWSER_SESSION_HYDRATE_TIMEOUT_MS
    }))

  const fallbackSession = buildFallbackDesktopSession(desktopSession)
  const hydrationKey = buildDesktopHostedBrowserSessionHydrationKey(desktopSession)

  if (
    !desktopSession ||
    desktopSession.error ||
    !desktopSession.isAuthenticated ||
    !desktopSession.sessionData?.accessToken ||
    !desktopSession.sessionData?.refreshToken
  ) {
    return fallbackSession
  }

  const cachedSession = getCachedDesktopHostedBrowserSession(hydrationKey)
  if (cachedSession !== undefined) {
    return cachedSession || fallbackSession
  }

  if (
    pendingDesktopHostedBrowserSessionHydration &&
    pendingDesktopHostedBrowserSessionHydrationKey === hydrationKey
  ) {
    return pendingDesktopHostedBrowserSessionHydration
  }

  const hydrationPromise = resolveDesktopHydratedSession(desktopSession)
    .then((session) => {
      const resolvedSession = session || fallbackSession
      cacheDesktopHostedBrowserSession(hydrationKey, resolvedSession)
      return resolvedSession
    })
    .finally(() => {
      if (pendingDesktopHostedBrowserSessionHydrationKey === hydrationKey) {
        pendingDesktopHostedBrowserSessionHydration = null
        pendingDesktopHostedBrowserSessionHydrationKey = ''
      }
    })

  pendingDesktopHostedBrowserSessionHydration = hydrationPromise
  pendingDesktopHostedBrowserSessionHydrationKey = hydrationKey

  return hydrationPromise
}

export async function getBootstrapBrowserSession(
  desktopSessionInput?: DesktopSessionSnapshot | null
): Promise<Session | null> {
  if (typeof window === 'undefined') {
    return null
  }

  if (isDesktopHost()) {
    try {
      return await hydrateDesktopHostedBrowserSession(desktopSessionInput)
    } catch (error) {
      console.warn('Failed to hydrate hosted desktop browser session during bootstrap:', error)
      return null
    }
  }

  const {
    data: { session },
    error
  } = await supabase.auth.getSession()

  if (error) {
    throw error
  }

  return session?.user ? session : null
}
